import React, { lazy, Suspense, useState } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import CustomButton from 'components/CustomButton';

const TemplatePreviewDialog = lazy(() => import('dialogs/TemplatePreviewDialog'));

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
  },
  headerSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  templateGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: theme.spacing(5),
    width: '100%',
    paddingBottom: theme.spacing(4),
  },
  templateCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '417px',
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.neutral['clr-300']}`,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    overflow: 'hidden',
    position: 'relative',
    '&:hover': {
      boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
      border: `1px solid ${theme.palette.primary.main}`,
    },
    '&:hover $eyeButton': {
      opacity: 1,
    },
    '&.selected': {
      border: `2px solid ${theme.palette.primary.main}`,
      boxShadow: '0px 0px 12px 0px #D2D2D28C',
    },
  },
  templatePreview: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.neutral['clr-100'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  eyeButton: {
    position: 'absolute',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    backgroundColor: theme.palette.shades['clr-white-900'],
    border: `1px solid ${theme.palette.neutral['clr-300']}`,
    opacity: 0,
    transition: 'opacity 0.3s ease',
    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      backgroundColor: theme.palette.primary['clr-50'],
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    padding: theme.spacing(1, 0),
  },
}));

const TEMPLATE_IMAGES = {
  template_1: 'https://languify-assets-v02.s3.ap-south-1.amazonaws.com/images/resume_template1.png',
  template_2: 'https://languify-assets-v02.s3.ap-south-1.amazonaws.com/images/resume_template2.png',
  template_3: 'https://languify-assets-v02.s3.ap-south-1.amazonaws.com/images/resume_template3.png',
  template_4: 'https://languify-assets-v02.s3.ap-south-1.amazonaws.com/images/resume_template4.png',
  template_5: 'https://languify-assets-v02.s3.ap-south-1.amazonaws.com/images/resume_template5.png',
  template_6: 'https://languify-assets-v02.s3.ap-south-1.amazonaws.com/images/resume_template6.png',
};

const templates = [
  { _id: 'template_1', name: 'Template 1' },
  { _id: 'template_2', name: 'Template 2' },
  { _id: 'template_3', name: 'Template 3' },
  { _id: 'template_4', name: 'Template 4' },
  { _id: 'template_5', name: 'Template 5' },
  { _id: 'template_6', name: 'Template 6' },
];

const SelectTemplate = ({ onSelect }) => {
  const classes = useStyles();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const handleTemplateClick = (templateId) => {
    setSelectedTemplate(templateId);
  };

  const handlePreview = (e, template) => {
    e.stopPropagation();
    setPreviewTemplate(template);
  };

  const handleContinue = () => {
    if (selectedTemplate) {
      onSelect(selectedTemplate);
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.headerSection}>
        <Typography variant='h5-semiBold' color='neutral.clr-900'>
          Choose your favorite template design
        </Typography>
        <Typography variant='body01-regular' color='neutral.clr-600'>
          You can always change your template later.
        </Typography>
      </Box>

      <Box className={classes.templateGrid}>
        {templates.map((template) => (
          <Box
            key={template._id}
            className={`${classes.templateCard} ${selectedTemplate === template._id ? 'selected' : ''}`}
            onClick={() => handleTemplateClick(template._id)}
          >
            <Box className={classes.templatePreview}>
              {TEMPLATE_IMAGES[template._id] ? (
                <img
                  src={TEMPLATE_IMAGES[template._id]}
                  alt={template.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Typography variant='body02-bold' color='neutral.clr-400'>
                  {template.name}
                </Typography>
              )}
            </Box>
            <IconButton
              className={classes.eyeButton}
              size='small'
              onClick={(e) => handlePreview(e, template)}
            >
              <VisibilityOutlinedIcon fontSize='small' color='primary' />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Box className={classes.footer}>
        <CustomButton
          size='medium'
          disabled={!selectedTemplate}
          onClick={handleContinue}
          sx={{ width: 'fit-content' }}
        >
          Continue
        </CustomButton>
      </Box>

      {previewTemplate && (
        <Suspense fallback={<CircularProgress />}>
          <TemplatePreviewDialog
            open={Boolean(previewTemplate)}
            onClose={() => setPreviewTemplate(null)}
            template={previewTemplate}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default SelectTemplate;
